import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  getAuthSession,
  isElectron,
  testDownloadSpeed,
  testUploadSpeed,
} from "./component/Helpers/common";
import { CLIENT_URL, LOGOUT_CODE_PARAM } from "./constants";
import { getTransport } from "./component/transport";
import AlertModal from "./component/models/AlertModal";
import {
  setIsLoggedInAccountActive,
  setErrorCode,
} from "./component/redux/authUserDetail";
import BelowHeader from "./component/BelowHeader";
import {
  clearUnreadThreadCount,
  openUserChat,
  socketAllEventsOpen,
  userAccountExist,
  userGetVerifiedHandler,
} from "./component/Helpers/chatCommon";
import { globalStrings } from "./component/util/translation/languages";
import { setElectronAppVersion } from "./component/redux/callSlice";
import {
  setActiveThreadChat,
  setDesktopSystemStats,
} from "./component/redux/chatSlice";
import { _desktopSystemStats } from "./SocketIo";
import { clearStore } from "./component/util/indexedDB";
import ClosedSessionPopup from "./component/models/ClosedSessionPopup";
import { sendHangupWhenMoveTab } from "./component/Helpers/CallCommon";
import { HangupCall } from "./component/Helpers/Lib";

const transport = getTransport();

export default function Dashboard() {
  const dispatch = useDispatch();
  let userOnlineIntervalID = null;
  let token = useParams();
  let loginToken = token.id;
  const [sessionTheme, setSessionTheme] = useState(false);
  const {
    authSuccess,
    authSession,
    errorCode,
    logout,
    IsLoggedInAccountActive,
    userLoginToken,
    chatMoveToNewTab,
    authSessionUser,
  } = useSelector((state) => state.authUserDetail);

  const {
    selectedLanguage,
    userStaticInfo,
    desktopSystemStats,
    groupList,
    socketData,
    isSocketConnected,
    attendeeList,
    attendeeData,
  } = useSelector((state) => state.chat);

  const { allAttendeeList, callDetail } = useSelector((state) => state.call);

  const attendeeListRef = useRef(attendeeList);
  const groupListRef = useRef(groupList);

  useEffect(() => {
    attendeeListRef.current = attendeeList;
  }, [allAttendeeList]);

  useEffect(() => {
    groupListRef.current = groupList;
  }, [groupList]);

  const [chatTabMoved, setChatTabMoved] = useState(false);

  const [downloadSpeed, setDownloadSpeed] = useState(-1);
  const [uploadSpeed, setUploadSpeed] = useState(-1);

  useEffect(() => {
    if (loginToken) {
      window
        .matchMedia("(prefers-color-scheme: dark)")
        .addEventListener("change", checkSessionTheme);
      checkDownloadSpeed();
      checkUploadSpeed();
      userAccountExist(loginToken);

      transport.on("event", (event) => {
        if (event.name === "send-app-version") {
          const electronAppVersionData =
            event?.data && event?.data.length > 0 && event?.data[0];
          dispatch(setElectronAppVersion(electronAppVersionData));
        }
        if (event.name === "system-info") {
          const systemInfoData =
            event?.data && event?.data.length > 0 && event?.data[0];
          dispatch(setDesktopSystemStats(systemInfoData));
        }
        if (event.name === "notification-chat-open") {
          const notificationData =
            event?.data && event?.data.length > 0 && event?.data[0];
          const chatRoomParam = notificationData?.documentID;
          const parentMsgId = notificationData?.parent_id;
          const isGroupNotification =
            notificationData?.type === "one-to-one-message" ? false : true;
          if (isGroupNotification) {
            const paramGroupData = groupListRef.current?.find(
              (v) => v?.groupId === chatRoomParam,
            );
            openUserChat(paramGroupData, isGroupNotification);
          } else {
            const paramAttendeeData = attendeeListRef.current?.find((v) =>
              chatRoomParam?.split("_")?.includes(v?.user_id),
            );

            openUserChat(paramAttendeeData, isGroupNotification);
          }

          if (parentMsgId && parentMsgId != "undefined") {
            dispatch(
              setActiveThreadChat({
                documentID: chatRoomParam,
                id: parentMsgId,
              }),
            );
          }

          clearUnreadThreadCount(
            chatRoomParam,
            parentMsgId,
            attendeeData?.company_id,
          );
        }
      });
    }
  }, []);

  const checkDownloadSpeed = async () => {
    let downloadSpeed = await testDownloadSpeed();
    setDownloadSpeed(downloadSpeed);
  };

  const checkUploadSpeed = async () => {
    let uploadSpeed = await testUploadSpeed();
    setUploadSpeed(uploadSpeed);
  };

  useEffect(() => {
    if (
      Object.keys(userStaticInfo).length > 0 &&
      Object.keys(desktopSystemStats).length > 0 &&
      downloadSpeed > -1 &&
      uploadSpeed > -1
    ) {
      const system_stats = { ...desktopSystemStats };
      system_stats["downloadSpeed"] = `${downloadSpeed} Mbps`;
      system_stats["uploadSpeed"] = `${uploadSpeed} Mbps`;
      const payload = {
        user_id: userStaticInfo?.user_id,
        company_id: userStaticInfo?.company_id,
        system_stats: system_stats,
      };
      _desktopSystemStats(payload);
    }
  }, [userStaticInfo, desktopSystemStats, downloadSpeed, uploadSpeed]);

  useEffect(() => {
    checkSessionTheme();
  });

  const checkSessionTheme = () => {
    if (window.matchMedia) {
      if (window.matchMedia("(prefers-color-scheme: dark)")?.matches) {
        setSessionTheme(true);
      } else {
        setSessionTheme(false);
      }
    }
  };

  useEffect(() => {
    if (sessionTheme === true) {
      document.body.classList.add("theme-dark");
    } else {
      document.body.classList.remove("theme-dark");
    }
  }, [sessionTheme]);

  useEffect(() => {
    if (loginToken) {
      if (isSocketConnected && socketData !== null) {
        socketAllEventsOpen(loginToken);
      }
    } else {
      let getSession = getAuthSession();
      if (!getSession) {
        if (isElectron) {
          transport.sendEvent({
            name: "video-conference-left",
          });
        } else {
          window.location = CLIENT_URL;
        }
      }
    }
  }, [isSocketConnected, socketData]);

  useEffect(() => {
    if (Object.keys(authSession).length > 0) {
      if (authSession?.status == 200) {
        userGetVerifiedHandler(authSession, loginToken, navigate);
      } else {
        dispatch(setIsLoggedInAccountActive(true));
        dispatch(setErrorCode(authSession.status));
      }
    }
  }, [authSession]);

  useEffect(() => {
    return () => {
      if (userOnlineIntervalID !== null) {
        clearInterval(userOnlineIntervalID);
      }
    };
  }, []);

  let navigate = useNavigate();

  useEffect(() => {
    if (logout) {
      dispatch(setIsLoggedInAccountActive(true));
      dispatch(setErrorCode(LOGOUT_CODE_PARAM));
    }
  }, [logout]);

  // useEffect(() => {
  //   if (authSessionUser) {
  //     const virtual_background = authSessionUser?.virtual_background ?? {};
  //     dispatch(setVirtualBackgroundOptions(virtual_background));
  //   }
  // }, [authSessionUser]);

  useEffect(() => {
    clearStore();
  }, []);

  useEffect(() => {
    if (chatMoveToNewTab) {
      // Check if the user is currently on an active call
      if (authSessionUser?.onCall) {
        // We retrieve the session data. If it was briefly deleted during the tab switch, we recreate it.
        // because some inner function need auth session details to hangup the call
        let authData = getAuthSession();
        let user_unique_key = authData?.user_unique_key;
        if (!user_unique_key) {
          // Prepare the session data again using the existing authSession state
          const initialState = {
            username: authSession?.data?.user_id,
            login_token: userLoginToken,
            user_unique_key: authSession?.data?.user_id,
            company_unique_key: authSession?.data?.company_id,
          };

          // Save the session back to localStorage so the app stays logged in
          localStorage.setItem("auth_session", JSON.stringify(initialState));

          // Update our local variable to use the newly restored key
          user_unique_key = initialState.user_unique_key;
        }

        // Prepare the details needed to end the call properly
        const props = {
          callDetails: callDetail,
          user_unique_key,
          groupList,
          callModalType:
            callDetail?.senderID === user_unique_key ? "outBound" : "inBound",
        };
        // Trigger the hangup function to notify other participants
        sendHangupWhenMoveTab(props, allAttendeeList);
        HangupCall();
        setChatTabMoved(true);
      } else {
        setChatTabMoved(true);
      }
    }
  }, [chatMoveToNewTab]);

  return (
    <>
      {chatTabMoved ? (
        <ClosedSessionPopup show={chatTabMoved} />
      ) : authSuccess &&
        isSocketConnected &&
        IsLoggedInAccountActive === false ? (
        <>
          <div className="bg--dark border-radius h-100">
            <BelowHeader />
          </div>
        </>
      ) : (
        <>
          <div className="loader_spinner">
            <div className="loader_spinner__box">
              <div className="spin"></div>
            </div>
          </div>
        </>
      )}

      {IsLoggedInAccountActive && (
        <AlertModal
          show={IsLoggedInAccountActive}
          closeModal={() => dispatch(setIsLoggedInAccountActive(true))}
          msg={
            errorCode == 100
              ? globalStrings[selectedLanguage]?.$dashboardError
              : `${globalStrings[selectedLanguage]?.$dashboardError1} ${errorCode})`
          }
          alertType={"accountInactive"}
          logOutButtonEnable={errorCode == "405" ? true : false}
          selectedLanguage={selectedLanguage}
        />
      )}
    </>
  );
}
